import React, { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { SERVICE_DELIVERY, SERVICE_KITPLAY, SERVICE_ORDER_DISPLAY, SERVICE_PICKUP, SERVICE_QRCODE_CUSTOMER } from '@constants';
import apiConfig from '@constants/apiConfig';
import useFetch from '@hooks/useFetch';
import useQueryParams from '@hooks/useQueryParams';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import ThirdPartyListPage from '@modules/restaurant/Devices/tabs/ThirdParty';
import routes from '@routes';
import { showErrorMessage } from '@services/notifyService';
import { Tabs } from 'antd';

import DigibesDeviceListPage from './tabs/digibes';
import KitplayDeviceTabPage from './tabs/kitplay';
import PickupDisplayDeviceTabPage from './tabs/pickupDisplay/tabs';
import PiServerDeviceListPage from './tabs/piServer';
import QrLiveDeviceListPage from './tabs/qrlive';

const message = defineMessages({
    objectName: 'Devices',
});

const TAB_CONFIG = {
    piServer: {
        key: 'devices',
        label: commonMessage.devices,
        component: PiServerDeviceListPage,
    },
    thirdParty: {
        key: 'third-party',
        label: commonMessage.thirdParty,
        component: ThirdPartyListPage,
    },
    ordercon: {
        key: 'ordercon',
        label: commonMessage.ordercon,
        component: DigibesDeviceListPage,
        services: [SERVICE_PICKUP, SERVICE_DELIVERY],
    },
    qrLive: {
        key: 'qr-live',
        label: commonMessage.qrLive,
        component: QrLiveDeviceListPage,
        service: SERVICE_QRCODE_CUSTOMER,
    },
    kitplay: {
        key: 'kitplay',
        label: commonMessage.kitplay,
        component: KitplayDeviceTabPage,
        service: SERVICE_KITPLAY,
    },
    pickupDisplay: {
        key: 'pickup-display',
        label: commonMessage.pickupDisplay,
        component: PickupDisplayDeviceTabPage,
        service: SERVICE_ORDER_DISPLAY,
    },
};

const DeviceTab = () => {
    const translate = useTranslate();
    const paramid = useParams();
    const { params, setQueryParams } = useQueryParams();
    const restaurantId = params.get('restaurantId');
    const restaurantName = params.get('restaurantName');

    const firstTabKey = Object.keys(TAB_CONFIG)[0]; 
    const activeTab = params.get('tab') || firstTabKey;

    const handleTabClick = (key) => {
        setQueryParams({ tab: key, restaurantId, restaurantName });
    };

    const [tabFilters, setTabFilters] = useState({});
    const setFilterForTab = (tabKey, filter) => {
        setTabFilters(prev => ({
            ...prev,
            [tabKey]: filter,
        }));
    };

    const [restaurantDetail, setRestaurantDetail] = useState(null);
    const { execute: executeGetRestaurantById } = useFetch(apiConfig.restaurant.getById, { 
        immediate: false,
    });

    const handleGetRestaurantById = (id) => {
        executeGetRestaurantById({
            pathParams: { id },
            onCompleted: (res) => {
                if (res.result === true) {
                    setRestaurantDetail(res.data);
                }
            },
            onError: (err) => {
                showErrorMessage(err, translate);
            },
        });
    };

    const [serverDetail, setServerDetail] = useState(null);
    const { execute: executeGetServerByRestaurantId } = useFetch(apiConfig.kitplayDevice.getByRestaurant, {
        immediate: false,
    });

    const handleGetServerByRestaurantId = (id) => {
        executeGetServerByRestaurantId({
            pathParams: { restaurantId: id },
            onCompleted: (res) => {
                if (res.result === true) {
                    setServerDetail(res.data);
                }
            },
            onError: (err) => {
                if (err.response?.data?.code === 'ERROR-DEVICE-0000'){
                    setServerDetail(null);
                } else {
                    showErrorMessage(err, translate);
                }
            },
        });
    };

    useEffect(() => {
        if (!restaurantId) return;

        handleGetRestaurantById(restaurantId);
        handleGetServerByRestaurantId(restaurantId);
    }, [restaurantId]);
    

    const tabItems = Object.values(TAB_CONFIG)
        .filter((tab) => {
            if (!serverDetail && (tab.key === 'kitplay' || tab.key === 'pickup-display')) {
                return false;
            }
            const availableServices = restaurantDetail?.services 
                ? restaurantDetail.services.split(';') 
                : [];
            if (tab.services && Array.isArray(tab.services)) {
                const hasAtLeastOneService = tab.services.some(srv => availableServices.includes(srv));
                if (!hasAtLeastOneService) {
                    return false;
                }
            }
            if (tab.service) {
                if (!availableServices.includes(tab.service)) {
                    return false;
                }
            }

            return true;
        })
        .map((tab) => {
            const TargetComponent = tab.component;
        
            return {
                key: tab.key,
                label: tab?.tabLabel ? translate.formatMessage(tab.tabLabel) : translate.formatMessage(tab.label),
                children: activeTab === tab.key && (
                    <TargetComponent
                        tabKey={tab.key}
                        restaurantId={restaurantId}
                        restaurant={restaurantDetail}
                        serverDetail={serverDetail}
                        savedFilter={tabFilters[tab.key]}
                        onFilterChange={(filter) => setFilterForTab(tab.key, filter)}
                        handleGetServerByRestaurantId={handleGetServerByRestaurantId}
                    />
                ),
            };
        });

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.digibesShop),
                    path: routes.shopAccountListPage.path,
                },
                {
                    breadcrumbName: translate.formatMessage(commonMessage.restaurant),
                    path: `${generatePath(routes.RestaurantByCustomerListPage.path, {
                        customerId: paramid.customerId,
                    })}`,
                },
                { breadcrumbName: restaurantName || translate.formatMessage(message.objectName) },
            ]}
        >
            <ListPage
                baseTable={
                    <Tabs
                        type="card"
                        onChange={handleTabClick}
                        destroyOnHidden
                        activeKey={activeTab}
                        items={tabItems}
                    />
                }
            />
        </PageWrapper>
    );
};

export default DeviceTab;